import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Alert, Button, DatePicker, Form, message, Modal, Popover } from 'antd';
import * as React from 'react';
import OWForm from '../ow_form/OWForm';
import { useMemo, useState } from 'react';
import { WORK_ORDERS_CRUD_ACTION_CREATORS } from '../../actions/work_orders_actions';
import { RangePickerProps } from 'antd/es/date-picker';
import moment from 'moment';
import {
	deferWorkOrderForBuyer,
	deferWorkOrderForSupplier,
	snoozeWorkOrderForBuyer,
	snoozeWorkOrderForSupplier,
} from '../../thunks/work_orders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { checkHasApprovalHierarchy, nullSafeGet } from '../../utils/DataAccessUtils';
import { isInApprovalHierarchy } from '../../utils/ApprovalHierarchyUtils';
import {
	getBuyerCompanySettingValue,
	isUserAllowedToAccess,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';

const dateFormat = 'dddd, MMM D, YYYY';

function DeferWorkOrder({
	//From Parent
	workOrder,
	buttonProps = {},
	buttonText = 'Defer Work Order',
	deferredForBudget = false,

	//From mapStateToProps
	companyConfig,
	userType,
	currentUser,
	createErrors,
	updateErrors,

	//mapDispatchToProps
	snoozeWorkOrder,
	deferWorkOrderForBudget,
}) {
	const [form] = Form.useForm();
	const [visible, setVisible] = useState(false);
	const [loading, setLoading] = useState(false);

	const isAllowed = getBuyerCompanySettingValue(userType)(
		'workOrder.allowWorkOrderToBeDeferred',
		companyConfig,
		currentUser,
		false
	);
	const isApprovalAllowed = useMemo(
		() =>
			checkHasApprovalHierarchy(workOrder)
				? isInApprovalHierarchy(currentUser, nullSafeGet('approvalHierarchy', workOrder))
				: isUserAllowedToAccess(userType)(
						PERMISSION_NAMES.CAN_APPROVE_WORK_ORDERS,
						companyConfig,
						currentUser.roles
				  ),
		[]
	);

	const disabledDate: RangePickerProps['disabledDate'] = (current) => {
		// Can not select days before today and today
		return current && current < moment().endOf('day');
	};
	const handleSubmit = (values) => {
		const entity = {
			id: parseInt(values.workOrderId),
			snoozeUntil: values.deferDate,
		};
		const fnToCall = deferredForBudget ? deferWorkOrderForBudget : snoozeWorkOrder;

		fnToCall(entity)
			.catch(() => {
				message.error(`Error Deferring Work Order.`);
			})
			.then(() => {
				message.success(`Deferred Work Order.`);
			})
			.finally(() => {
				setVisible(false);
				setLoading(false);
			});
	};

	return isAllowed ? (
		<>
			<Popover
				title={isApprovalAllowed ? null : `Why can't I defer?`}
				content={isApprovalAllowed ? null : 'You are not in the approval hierarchy.'}
			>
				<Button
					key={'defer'}
					onClick={() => setVisible(true)}
					disabled={!isApprovalAllowed}
					{...buttonProps}
				>
					{buttonText}
				</Button>
			</Popover>
			<Modal
				visible={visible}
				title={'Defer this work order?'}
				okText={'Defer'}
				onCancel={() => setVisible(false)}
				onOk={form.submit}
				closable={false}
				confirmLoading={loading}
			>
				<OWForm
					form={form}
					layout="vertical"
					className="supplierForm"
					preserve={false}
					actionCreators={WORK_ORDERS_CRUD_ACTION_CREATORS}
					onFinish={handleSubmit}
				>
					{createErrors.length > 0 ? (
						<Form.Item>
							<Alert message={createErrors.join(' ')} type="error" />
						</Form.Item>
					) : null}
					{updateErrors.length > 0 ? (
						<Form.Item>
							<Alert message={updateErrors.join(' ')} type="error" />
						</Form.Item>
					) : null}
					<Form.Item
						hidden={true}
						name={'workOrderId'}
						label="Work Order Id"
						initialValue={nullSafeGet('id', workOrder)}
						required={true}
					/>
					<Form.Item label="Defer Until" name={'deferDate'} format={dateFormat} required={false}>
						<DatePicker disabledDate={disabledDate} />
					</Form.Item>
				</OWForm>
			</Modal>
		</>
	) : null;
}

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	createErrors: state.buyer_contacts.createErrors,
	updateErrors: state.buyer_contacts.updateErrors,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	snoozeWorkOrder: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? snoozeWorkOrderForSupplier(entity)
				: snoozeWorkOrderForBuyer(entity)
		),
	deferWorkOrderForBudget: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? dispatch(deferWorkOrderForSupplier(entity))
				: dispatch(deferWorkOrderForBuyer(entity))
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(DeferWorkOrder)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
